import React, { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from "react-i18next";

import { Container, Row, Col, Button } from "react-bootstrap";
import { LoadingTable } from "../lazyLoading/LazyLoading";
import ContentSolid from "../global/ContentSolid";
import ContentTable from "../table/ContentTable";
import { MainContext } from "../../App";
import { Table } from "../table/Table";

import ModalConfirmDeletedRecords from "../global/modals/ModalConfirmDeletedRecords";
import ModalTag from './modals/ModalTag';

import { GetTagsPosts, DeleteTags } from "../../api/News";

//Custom styles section
const stylesCustom = {
  textDangerBold: {
    color: "#ea2c54",
    fontWeight: "bold",
    fontSize: "small",
  },
  textGrayBold: {
    color: "#c8cbcc",
    fontWeight: "bold",
    fontSize: "small",
  },
};

const TagsView = () => {
  const [t] = useTranslation(["global", "news", "table", "reports"]);
  const componentRef = useRef(); //Create the reference for printing
  const { permissions } = useContext(MainContext);

  const [showModalConfirmDeleted, setShowModalConfirmDeleted] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isAllRowsSelected, setIsAllRowsSelected] = useState(false);
  const [count, setCount] = useState(0);
  const [showModalTag, setShowModalTag] = useState(false);
  const [loadingView, setLoadingView] = useState(true);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectAllRows, setSelectAllRows] = useState({
    switch: false,
    value: false,
  });
  const [tagDetails, setTagDetails] = useState({});

  // Define table columns
  const columns = React.useMemo(
    () => [
      {
        Header: t("news:tagsPage.name"),
        accessor: "name",
        width: "48%",
      },
      {
        Header: t("news:tagsPage.description"),
        accessor: "description",
        width: "48%",
      },
    ],
    [loadingView]
  );

  //Saves the data for the table in memory, and is not updated unless useMemo identifies a change in the data
  const data = React.useMemo(() => tags, [loading]);

  /**
   * Function to get the list of tags
   */
  const getTags = () => {
    setLoading(true);
    GetTagsPosts({ no_limit: true })
      .then((response) => {
        if (!response.status) return;
        let tagList = response.data.map((tag) => {
          return {
            index: tag.id,
            id: tag.id,
            name: tag.name,
            description: tag.description,
          };
        });
        setTags(tagList);
      })
      .finally(() => {
        setLoadingView(false);
        setLoading(false);
      });
  }

  /**
   * Function to delete selected records
   */
  const deleteTags = () => {
    const payload = {
      ids: selectedRowIds.map((news) => news.original.id),
    }
    DeleteTags(payload)
      .then((response) => {
        if (!response.status) {
          return;
        }
        getTags();
        setSelectedRowIds([]);
        setIsAllRowsSelected(false);
        setShowModalConfirmDeleted(false);
      })
      .finally(() => { });
  }

  /**
   * Purpose: Function to select or deselect all records
   */
  const selectAllRecords = () => {
    let selectAll = {
      switch: !selectAllRows.switch,
      value: !selectAllRows.value,
    };
    setSelectAllRows(selectAll);
  };

  /**
   * Function to open the modal to edit a news
   * @param {object} news 
   */
  const rowOnclick = (news) => {
    const editNews = {
      id: news.id,
      name: news.name,
      description: news.description,
    }
    setTagDetails(editNews);
    setShowModalTag(true);
  };

  useEffect(() => {
    getTags();
  }, []);

  /**
* Description: Effect to know if all prospects are selected
*/
  useEffect(() => {
    let selectAll = {
      switch: selectAllRows.switch,
      value: isAllRowsSelected,
    };
    setSelectAllRows(selectAll);
  }, [selectedRowIds]);

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col md={6}>
          {permissions.news_labels.delete &&
            <Button
              id="downloadStudentCredentialsBtn"
              variant="outline-secondary"
              onClick={() => setShowModalConfirmDeleted(true)}
              disabled={!selectedRowIds.length}
            >
              {t("global:buttons.eliminate")}
            </Button>
          }
          {/* Total counter of records and selected records in Classes table */}
          {selectedRowIds.length ? (
            selectedRowIds.length == 1 ? (
              <p className="ms-3 d-inline" style={stylesCustom.textDangerBold}>
                {t("table:textTableViews.selectedRecord", {
                  countSelected: selectedRowIds.length,
                })}
              </p>
            ) : (
              <p className="ms-3 d-inline" style={stylesCustom.textDangerBold}>
                {t("table:textTableViews.selectedRecords", {
                  countSelected: selectedRowIds.length,
                })}
              </p>
            )
          ) : (
            <p className="ms-3 d-inline" style={stylesCustom.textGrayBold}>
              {t("table:textTableViews.totalRecords", {
                count: count,
              })}
            </p>
          )}
        </Col>
        <Col md={6}>
          {permissions.news_labels.create &&
            <Button
              id="downloadStudentCredentialsBtn"
              className="float-end"
              onClick={() => { setShowModalTag(true) }}
            >
              {t("news:tagsPage.create")}
            </Button>
          }
        </Col>
      </Row>
      {loadingView ? (
        <ContentSolid>
          <LoadingTable />
        </ContentSolid>
      ) : (
        <ContentTable className="mt-3" startColumFilter={1} lastColumFilter={2}>
          <Table
            ref={componentRef}
            columns={columns}
            data={data}
            loading={loading}
            setSelectedRowIds={setSelectedRowIds}
            setIsAllRowsSelected={setIsAllRowsSelected}
            setRowsDisplayed={setCount}
            selectAllRows={selectAllRows}
            selectAllRecords={selectAllRecords}
            rowSelect={true}
            enableCheckToSelectAllRecords={true}
            rowOnclick={permissions.news_labels.edit ? rowOnclick : null}
          ></Table>
        </ContentTable>
      )}
      {/* Modals */}
      {showModalConfirmDeleted &&
        <ModalConfirmDeletedRecords
          showModalConfirmDeleted={showModalConfirmDeleted}
          setShowModalConfirmDeleted={setShowModalConfirmDeleted}
          confirmAction={deleteTags}
          title={t("news:modalTag.deleteTags")}
          message={t("news:modalTag.deleteTagsMenssage")}
          confirmText={t("global:buttons.eliminate")}
        />
      }
      {showModalTag &&
        <ModalTag
          showModalTag={showModalTag}
          setShowModalTag={() => {
            setTagDetails({})
            setShowModalTag(false)
          }}
          getTags={getTags}
          tagDetails={tagDetails}
        />
      }
    </Container>
  );
}

export default TagsView;
TagsView.propTypes = {
};